// Centralized "where should the user go after sign-in/sign-up" intent.
//
// Why: previously each entry point (AuthModal `redirectTo`, `pending_checkout`,
// quiz/diagnostico CTAs, etc.) handled redirects on its own and OAuth could
// land users on `panel.retaillemon.com/#` (blank).
//
// Pattern:
//   1. The component that triggers auth calls `setPostAuthIntent({...})`
//      *before* opening AuthModal or starting OAuth.
//   2. The global `usePostAuthRedirect` hook (mounted once in App.tsx) reads
//      the intent the moment `isAuthenticated` flips true and routes the user
//      to the right place — defaulting to `/dashboard`.
//   3. Cross-domain (www → panel) is handled by also encoding the intent in
//      the OAuth `redirectTo` query as `?intent=<base64>` so it survives the
//      round-trip even when localStorage is per-origin.

export type PostAuthIntent =
  | { kind: "path"; path: string }
  | { kind: "quiz" }
  | { kind: "diagnostico" };

const KEY = "post_auth_intent";

const isBrowser = () => typeof window !== "undefined";

export const setPostAuthIntent = (intent: PostAuthIntent): void => {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(KEY, JSON.stringify(intent));
  } catch {
    /* ignore quota / private-mode errors */
  }
};

export const peekPostAuthIntent = (): PostAuthIntent | null => {
  if (!isBrowser()) return null;
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as PostAuthIntent) : null;
  } catch {
    return null;
  }
};

export const consumePostAuthIntent = (): PostAuthIntent | null => {
  const intent = peekPostAuthIntent();
  if (intent && isBrowser()) {
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
  }
  return intent;
};

export const clearPostAuthIntent = (): void => {
  if (!isBrowser()) return;
  try { localStorage.removeItem(KEY); } catch { /* ignore */ }
};

// ── Cross-origin transport ──
// Encode the intent in a URL query so it can hop www → panel during OAuth.

export const encodeIntentForUrl = (intent: PostAuthIntent): string => {
  // base64url for URL safety
  const json = JSON.stringify(intent);
  if (typeof btoa !== "undefined") {
    return btoa(unescape(encodeURIComponent(json)))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
  }
  return encodeURIComponent(json);
};

export const decodeIntentFromUrl = (encoded: string): PostAuthIntent | null => {
  try {
    let json: string;
    if (typeof atob !== "undefined" && /^[A-Za-z0-9_-]+$/.test(encoded)) {
      const b64 = encoded.replace(/-/g, "+").replace(/_/g, "/");
      json = decodeURIComponent(escape(atob(b64)));
    } else {
      json = decodeURIComponent(encoded);
    }
    const parsed = JSON.parse(json);
    if (parsed && typeof parsed === "object" && typeof parsed.kind === "string") {
      return parsed as PostAuthIntent;
    }
    return null;
  } catch {
    return null;
  }
};

export const intentToPath = (intent: PostAuthIntent | null): string => {
  if (!intent) return "/dashboard";
  switch (intent.kind) {
    case "path":
      return intent.path || "/dashboard";
    case "quiz":
      return "/dashboard?reopen=quiz";
    case "diagnostico":
      return "/dashboard?reopen=diagnostico";
    default:
      return "/dashboard";
  }
};
